import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, Loader2, Trash2, Menu, Plus, MessageSquare, Volume2, VolumeX, Settings as SettingsIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const USER_KEY = 'pragya_user_id';
const ACTIVE_SESSION_KEY = 'pragya_active_session';
const PRAGYA_LOGO = '/pragya-logo.png';

function getOrCreateUserId() {
  let uid = localStorage.getItem(USER_KEY);
  if (!uid) {
    uid = 'user-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
    localStorage.setItem(USER_KEY, uid);
  }
  return uid;
}

const SUGGESTED = [
  'गायत्री मन्त्र का अर्थ क्या है?',
  'यज्ञ का महत्व बताइए',
  'युग निर्माण योजना क्या है?',
  '24 गायत्री शक्तिपीठ कहाँ हैं?',
  'पंचकोश साधना समझाइए',
];

function formatTime(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'अभी';
    if (diff < 3600) return `${Math.floor(diff / 60)} मिनट पहले`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} घंटे पहले`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} दिन पहले`;
    return d.toLocaleDateString('hi-IN');
  } catch { return ''; }
}

export default function PragyaAI() {
  const [open, setOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [userId] = useState(() => getOrCreateUserId());
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(ACTIVE_SESSION_KEY) || '');
  const [sessions, setSessions] = useState([]);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(() => localStorage.getItem('pragya_voice_uri') || '');
  const [showVoicePicker, setShowVoicePicker] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const utteranceRef = useRef(null);

  // Load available voices
  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const loadVoices = () => {
      const allVoices = window.speechSynthesis.getVoices();
      // Prefer Hindi/Indian voices but include all
      const sorted = [...allVoices].sort((a, b) => {
        const aHi = a.lang === 'hi-IN' ? 0 : a.lang.startsWith('hi') ? 1 : a.lang.startsWith('en-IN') ? 2 : 3;
        const bHi = b.lang === 'hi-IN' ? 0 : b.lang.startsWith('hi') ? 1 : b.lang.startsWith('en-IN') ? 2 : 3;
        return aHi - bHi;
      });
      setVoices(sorted);
    };
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
  }, []);

  // Stop speech on unmount or when chat closes
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (!open && typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setSpeakingIdx(null);
    }
  }, [open]);

  // Strip markdown/emoji for TTS
  const cleanForSpeech = (text) => {
    return (text || '')
      .replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '') // emojis
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/`(.*?)`/g, '$1')
      .replace(/📖|🙏|✨|💡|⚠️|❌|✅/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const toggleSpeak = (text, idx) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      toast.error('इस ब्राउज़र में voice support नहीं है।');
      return;
    }
    const synth = window.speechSynthesis;
    if (speakingIdx === idx) {
      synth.cancel();
      setSpeakingIdx(null);
      return;
    }
    synth.cancel();
    const utter = new SpeechSynthesisUtterance(cleanForSpeech(text));
    utter.lang = 'hi-IN';
    utter.rate = 0.95;
    utter.pitch = 1;
    // Use user-selected voice if any, else pick best Hindi/Indian
    const allVoices = synth.getVoices();
    let voice = null;
    if (selectedVoice) {
      voice = allVoices.find(v => v.voiceURI === selectedVoice);
    }
    if (!voice) {
      voice = allVoices.find(v => v.lang === 'hi-IN')
        || allVoices.find(v => v.lang.startsWith('hi'))
        || allVoices.find(v => v.lang === 'en-IN')
        || allVoices.find(v => v.lang.startsWith('en-IN'));
    }
    if (voice) utter.voice = voice;
    utter.onend = () => setSpeakingIdx(null);
    utter.onerror = () => setSpeakingIdx(null);
    utteranceRef.current = utter;
    synth.speak(utter);
    setSpeakingIdx(idx);
  };

  const pickVoice = (voiceURI) => {
    setSelectedVoice(voiceURI);
    localStorage.setItem('pragya_voice_uri', voiceURI);
    // Test with sample
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance('नमस्ते, यह आवाज़ का परीक्षण है।');
      utter.lang = 'hi-IN';
      const v = window.speechSynthesis.getVoices().find(x => x.voiceURI === voiceURI);
      if (v) utter.voice = v;
      window.speechSynthesis.speak(utter);
    }
  };

  const loadSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pragya/sessions`, { params: { user_id: userId } });
      if (Array.isArray(res.data)) setSessions(res.data);
    } catch (e) {
      console.error('Failed to load sessions', e);
    }
  }, [userId]);

  const loadHistory = useCallback(async (sid) => {
    if (!sid) { setMessages([]); return; }
    try {
      const res = await axios.get(`${API}/pragya/history/${sid}`);
      if (Array.isArray(res.data)) setMessages(res.data);
    } catch (e) {
      console.error('Failed to load history', e);
      setMessages([]);
    }
  }, []);

  // Load sessions when opened
  useEffect(() => {
    if (open) {
      loadSessions();
      if (sessionId) loadHistory(sessionId);
    }
  }, [open, loadSessions, loadHistory, sessionId]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    if (open && !sidebarOpen) setTimeout(() => inputRef.current?.focus(), 200);
  }, [open, sidebarOpen]);

  const switchSession = (sid) => {
    setSessionId(sid);
    localStorage.setItem(ACTIVE_SESSION_KEY, sid);
    loadHistory(sid);
    setSidebarOpen(false);
  };

  const newChat = () => {
    setSessionId('');
    localStorage.removeItem(ACTIVE_SESSION_KEY);
    setMessages([]);
    setSidebarOpen(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    const userMsg = { role: 'user', content: msg, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/pragya/chat`, {
        session_id: sessionId || undefined,
        user_id: userId,
        message: msg,
      }, { timeout: 120000 });

      const reply = res.data.reply || '';
      const newSid = res.data.session_id;
      const assistantMsg = { role: 'assistant', content: reply, timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, assistantMsg]);

      if (newSid && newSid !== sessionId) {
        setSessionId(newSid);
        localStorage.setItem(ACTIVE_SESSION_KEY, newSid);
      }
      loadSessions();
    } catch (e) {
      console.error('Pragya AI error:', e);
      const errMsg = e.code === 'ECONNABORTED'
        ? 'अनुरोध समय समाप्त (timeout)। कृपया पुनः प्रयास करें।'
        : e.response?.data?.detail
          ? `त्रुटि: ${e.response.data.detail}`
          : 'प्रज्ञा AI से संपर्क नहीं हो पाया। कृपया पुनः प्रयास करें।';
      toast.error(errMsg);
      setMessages(prev => prev.slice(0, -1));
      setInput(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const deleteCurrentSession = async () => {
    if (!sessionId) return;
    if (!window.confirm('यह बातचीत हमेशा के लिए हट जाएगी। क्या निश्चित हैं?')) return;
    try {
      await axios.delete(`${API}/pragya/sessions/${sessionId}`);
      toast.success('बातचीत हटाई गई।');
      newChat();
      loadSessions();
    } catch (e) {
      toast.error('हटाने में त्रुटि।');
    }
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm('यह बातचीत हटाएं?')) return;
    try {
      await axios.delete(`${API}/pragya/sessions/${sid}`);
      if (sid === sessionId) newChat();
      loadSessions();
      toast.success('हटाई गई।');
    } catch (err) {
      toast.error('त्रुटि।');
    }
  };

  const goHome = () => {
    setOpen(false);
    setSidebarOpen(false);
    window.location.assign('/');
  };

  const exitChat = () => {
    setOpen(false);
    setSidebarOpen(false);
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-4 sm:bottom-6 sm:right-6 z-40 bg-white rounded-full shadow-xl w-16 h-16 flex items-center justify-center hover:scale-105 transition-transform pulse-glow border-2 border-orange-300 overflow-hidden"
          aria-label="Pragya AI"
          title="प्रज्ञा AI सहायक"
        >
          <img src={PRAGYA_LOGO} alt="Pragya" className="w-full h-full object-cover" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-6 sm:right-6 z-50 sm:w-[460px] sm:h-[640px] sm:max-h-[85vh] bg-white sm:rounded-2xl shadow-2xl border border-orange-200 flex overflow-hidden">

          {/* Sidebar */}
          <div className={`${sidebarOpen ? 'w-64' : 'w-0'} transition-all duration-300 bg-orange-50 border-r border-orange-100 flex flex-col overflow-hidden shrink-0`}>
            <div className="p-3 border-b border-orange-100 shrink-0">
              <button
                onClick={newChat}
                className="w-full flex items-center justify-center gap-2 saffron-gradient text-white py-2 rounded-lg font-medium text-sm hover:opacity-95 transition-opacity"
              >
                <Plus className="w-4 h-4" />
                <span className="devanagari-ui">नई बातचीत</span>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {sessions.length === 0 ? (
                <div className="text-center py-6 px-2">
                  <MessageSquare className="w-8 h-8 text-orange-300 mx-auto mb-2" />
                  <p className="text-xs text-stone-500 devanagari-ui">कोई पुरानी बातचीत नहीं</p>
                </div>
              ) : (
                <ul className="space-y-1">
                  {sessions.map(s => (
                    <li key={s.session_id}>
                      <button
                        onClick={() => switchSession(s.session_id)}
                        className={`group w-full text-left px-2.5 py-2 rounded-lg transition-colors ${
                          s.session_id === sessionId
                            ? 'bg-orange-200/70 border border-orange-300'
                            : 'hover:bg-orange-100 border border-transparent'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <MessageSquare className="w-3.5 h-3.5 text-orange-600 mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-stone-800 devanagari-ui line-clamp-2 leading-tight">{s.title}</p>
                            <p className="text-[10px] text-stone-500 mt-0.5 devanagari-ui">{formatTime(s.updated_at)} · {s.message_count} संदेश</p>
                          </div>
                          <button
                            onClick={(e) => deleteSession(s.session_id, e)}
                            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded text-red-500 transition-opacity shrink-0"
                            aria-label="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Main chat area */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Header */}
            <div className="saffron-gradient text-white px-3 py-3 flex items-center gap-2 shrink-0">
              <button
                onClick={() => setSidebarOpen(o => !o)}
                className="p-2 rounded-full hover:bg-white/20 transition-colors shrink-0"
                aria-label="Toggle sidebar"
                title="इतिहास"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden shrink-0 border-2 border-white/40">
                <img src={PRAGYA_LOGO} alt="Pragya" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold devanagari-ui text-sm sm:text-base truncate">प्रज्ञा AI</h3>
                <p className="text-[10px] sm:text-xs text-orange-50/90 devanagari-ui truncate">पं. श्रीराम शर्मा आचार्य · AWGP</p>
              </div>
              {sessionId && messages.length > 0 && (
                <button
                  onClick={deleteCurrentSession}
                  className="p-2 rounded-full hover:bg-white/20 transition-colors shrink-0"
                  aria-label="Delete chat"
                  title="यह बातचीत हटाएं"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setShowVoicePicker(true)}
                className="p-2 rounded-full hover:bg-white/20 transition-colors shrink-0"
                aria-label="Voice settings"
                title="आवाज़ चुनें"
              >
                <SettingsIcon className="w-4 h-4" />
              </button>
              <button
                onClick={goHome}
                className="px-2 py-1 rounded-full hover:bg-white/20 transition-colors text-xs devanagari-ui shrink-0"
                aria-label="Home"
                title="Home"
              >
                Home
              </button>
              <button
                onClick={exitChat}
                className="px-2 py-1 rounded-full hover:bg-white/20 transition-colors text-xs devanagari-ui shrink-0"
                aria-label="Exit"
                title="Exit"
              >
                Exit
              </button>
              <button
                onClick={exitChat}
                className="p-2 rounded-full hover:bg-white/20 transition-colors shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 bg-orange-50/30">
              {messages.length === 0 && (
                <div className="text-center py-6">
                  <div className="w-24 h-24 mx-auto rounded-full overflow-hidden mb-3 border-4 border-orange-200 shadow-md">
                    <img src={PRAGYA_LOGO} alt="Pragya" className="w-full h-full object-cover" />
                  </div>
                  <h4 className="font-bold text-orange-950 devanagari-ui mb-1">नमस्ते! 🙏</h4>
                  <p className="text-sm text-stone-600 devanagari-ui mb-4 px-2">
                    पं. श्रीराम शर्मा आचार्य जी एवं AWGP साहित्य से सम्बंधित कोई भी प्रश्न पूछें
                  </p>
                  <div className="space-y-1.5 text-left">
                    <p className="text-xs font-semibold text-orange-700 devanagari-ui mb-1">सुझाए गए प्रश्न:</p>
                    {SUGGESTED.map((q, i) => (
                      <button
                        key={i}
                        onClick={() => sendMessage(q)}
                        className="block w-full text-left px-3 py-2 bg-white hover:bg-orange-100 text-sm text-stone-700 rounded-lg border border-orange-200 transition-colors devanagari-ui"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, idx) => (
                <div key={idx} className={`mb-3 flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] ${m.role === 'user' ? 'order-1' : ''}`}>
                    <div
                      className={`px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap devanagari-ui ${
                        m.role === 'user'
                          ? 'bg-orange-600 text-white rounded-br-sm'
                          : 'bg-white text-stone-800 border border-orange-100 rounded-bl-sm'
                      }`}
                      style={{ lineHeight: 1.6 }}
                    >
                      {m.content}
                    </div>
                    {m.role === 'assistant' && (
                      <button
                        onClick={() => toggleSpeak(m.content, idx)}
                        className="mt-1 ml-1 inline-flex items-center gap-1 text-xs text-orange-700 hover:text-orange-900 hover:bg-orange-100 px-2 py-1 rounded-full transition-colors devanagari-ui"
                        aria-label="Speak"
                        title={speakingIdx === idx ? 'रोकें' : 'सुनें'}
                      >
                        {speakingIdx === idx ? (
                          <><VolumeX className="w-3.5 h-3.5" /> रोकें</>
                        ) : (
                          <><Volume2 className="w-3.5 h-3.5" /> सुनें</>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start mb-3">
                  <div className="bg-white border border-orange-100 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 text-orange-600 animate-spin" />
                    <span className="text-sm text-stone-600 devanagari-ui">प्रज्ञा सोच रही है...</span>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-orange-100 p-3 bg-white shrink-0">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="अपना प्रश्न लिखें..."
                  rows={1}
                  className="flex-1 resize-none border border-orange-200 focus:border-orange-400 focus:outline-none rounded-xl px-3 py-2.5 text-sm devanagari-ui max-h-24 bg-orange-50/40"
                  style={{ minHeight: '42px' }}
                  disabled={loading}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || loading}
                  className="saffron-gradient text-white w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed shrink-0 hover:scale-105 transition-transform"
                  aria-label="Send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[10px] text-stone-400 mt-1.5 text-center devanagari-ui">
                केवल AWGP / आचार्य जी साहित्य आधारित उत्तर
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Voice Picker Dialog */}
      {showVoicePicker && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => setShowVoicePicker(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="saffron-gradient text-white px-4 py-3 flex items-center gap-2">
              <Volume2 className="w-5 h-5" />
              <h3 className="font-bold devanagari-ui flex-1">आवाज़ चुनें (TTS)</h3>
              <button onClick={() => setShowVoicePicker(false)} className="p-1 hover:bg-white/20 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <p className="text-xs text-stone-600 devanagari-ui mb-3 bg-orange-50 border border-orange-200 rounded p-2">
                💡 अपने device से उपलब्ध आवाज़ें — Hindi/Indian voices ऊपर दिख रही हैं। चुनने पर sample सुनाई देगा।
              </p>
              {voices.length === 0 ? (
                <p className="text-sm text-stone-500 text-center py-6 devanagari-ui">कोई आवाज़ उपलब्ध नहीं।</p>
              ) : (
                <ul className="space-y-1">
                  {voices.map((v) => {
                    const isHindi = v.lang === 'hi-IN' || v.lang.startsWith('hi');
                    const isIndian = v.lang.includes('IN');
                    const isSelected = v.voiceURI === selectedVoice;
                    return (
                      <li key={v.voiceURI}>
                        <button
                          onClick={() => pickVoice(v.voiceURI)}
                          className={`w-full text-left p-2.5 rounded-lg flex items-center gap-2 transition-colors ${
                            isSelected ? 'bg-orange-100 border-2 border-orange-400' : 'hover:bg-orange-50 border border-stone-200'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-stone-800 truncate">{v.name}</p>
                            <p className="text-xs text-stone-500">{v.lang} {v.localService ? '· local' : '· network'}</p>
                          </div>
                          {isHindi && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">हिंदी</span>}
                          {isIndian && !isHindi && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Indian</span>}
                          {isSelected && <span className="text-[10px] bg-orange-600 text-white px-1.5 py-0.5 rounded">चयनित</span>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
